import { API_URL, NEXTAUTH_API_URL } from "@/lib/config";
import { type ReactNode, createContext, useContext, useEffect, useState } from "react";
type User = {
  id: string;
  email: string;
  name?: string;
  image?: string;
  about?: string;
  coverUrl?: string;
  organizationId?: string;
};
type AuthContextType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  logout: () => void;
  addAuthHeaders: (headers?: HeadersInit) => HeadersInit;
  setUser: (user: User | null) => void;
};
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  logout: () => { },
  addAuthHeaders: () => ({}),
  setUser: () => { },
});
export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  // Load user from localStorage on mount (if available)
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem("auth_user");
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error loading user from localStorage:", error);
    }
  }, []);
  useEffect(() => {
    if (!user) {
      async function fetchAuthentication() {
        try {
          // STEP 1: Check Authentication Identity
          const redirectUrl = window.location.href;
          // Calling the auth service which runs on a separate server (e.g. 3001)
          const authResponse = await fetch(`${NEXTAUTH_API_URL}/api/auth/isAuthenticated`, {
            credentials: "include",
          });

          // The external auth service returns 401 or 403 when not authenticated, 
          // which causes .ok to be false, but it still returns a valid JSON payload.
          if (!authResponse.ok && authResponse.status !== 401 && authResponse.status !== 403) {
            throw new Error(`Authentication request failed with status: ${authResponse.status}`);
          }

          const authData = await authResponse.json();
          const isUserAuthenticated = authData.isAuthenticated;

          setIsAuthenticated(Boolean(isUserAuthenticated));

          // If not authenticated, halt execution and redirect (skip for public routes)
          if (!isUserAuthenticated) {
            const currentPath = window.location.pathname;
            const isPublicRoute =
              /^\/public\/note\/[^/]+\/?$/.test(currentPath) ||
              currentPath.startsWith("/form/public/") ||
              currentPath.startsWith("/capture/");
            if (!isPublicRoute) {
              window.location.href = `${NEXTAUTH_API_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
            }
            return;
          }

          // STEP 2: Fetch User Profile Document (Requires Auth Context)
          if (isUserAuthenticated && authData.user?.user?.email) {
            const email = authData.user.user.email;
            // SECURITY (CRIT-01): No x-user-email header. The browser ships
            // the NextAuth session cookie automatically via credentials:include
            // (same-site cross-port: localhost:3000 -> localhost:3002 is
            // SameSite=Lax-compatible). Backend auth.guard derives identity
            // from the cookie's JWE payload.
            const userResponse = await fetch(
              `${API_URL}/api/user/get/byemail/${encodeURIComponent(email)}`,
              { credentials: "include" },
            );

            if (userResponse.ok) {
              const fetchedUser = await userResponse.json();
              const userData: User = {
                id: fetchedUser.id || fetchedUser._id,
                email: fetchedUser.email,
                name: fetchedUser.name,
                image: fetchedUser.image,
                about: fetchedUser.about,
                coverUrl: fetchedUser.coverUrl,
                organizationId: fetchedUser.organizationId,
              };
              setUser(userData);
              localStorage.setItem("auth_user", JSON.stringify(userData));
            } else {
              // Fallback to basic auth provider data if DB profile fetch fails
              console.warn("Failed to fetch fresh user data from DB, using auth provider fallback");
              const userData: User = {
                id: authData.user.user.id || authData.user.user._id,
                email: authData.user.user.email,
                name: authData.user.user.name,
                image: authData.user.user.image,
                about: authData.user.user.about,
                coverUrl: authData.user.user.coverUrl,
                organizationId: authData.user.user.organizationId,
              };
              setUser(userData);
              localStorage.setItem("auth_user", JSON.stringify(userData));
            }
          }
        } catch (error) {
          console.error("Authentication or User Fetch error:", error);
          setIsAuthenticated(false);
        } finally {
          // STEP 3: Loading Complete (Triggers Workspace fetch downstream)
          setIsLoading(false);
        }
      }
      fetchAuthentication();
    }
  }, [user]);
  const logout = () => {
    // Clear localStorage
    localStorage.removeItem("auth_user");
    setUser(null);
    setIsAuthenticated(false);
    // Redirect to logout
    window.location.href = `${NEXTAUTH_API_URL}/api/auth/signout`;
  };
  // SECURITY (CRIT-01): The backend AuthGuard no longer trusts
  // x-user-email / x-user-name / x-user-image (they were a one-line
  // impersonation primitive). Identity now flows exclusively via the
  // NextAuth session cookie. We keep this helper as a no-op so existing
  // call sites compile without changes — callers still need to set
  // credentials:"include" on their fetch (or use fetchWithAuth).
  const addAuthHeaders = (headers: HeadersInit = {}) => headers;

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, logout, addAuthHeaders, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}
export function useAuth() {
  return useContext(AuthContext);
}
